import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import { DateTime } from 'luxon';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import { Text } from 'app/src/styles';

import { FIELDS, LINE_FIELDS, EMPTY_LINE } from './new';
import { UPDATE_TXN, DESTROY_TXN } from '../Txns/queries';

import { prepareTxnInput } from '../Txns/utils';

const EditCreditMemo = ({ txn, setShowModal, refetch }) => {
  const [nestedResources, setNestedResources] = useState(
    txn.txnLines.filter((txnLine, index) => index > 0)
  );
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const fields = { ...FIELDS };
  const lineFields = { ...LINE_FIELDS };

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: fields,
      resource: { ...txn, balanceAccountId: txn.txnLines[0].account.id },
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [updateTxn] = useMutation(UPDATE_TXN, {
    onCompleted: data => {
      if (data.updateTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.updateTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const [destroyTxn] = useMutation(
    DESTROY_TXN,
    { onCompleted: () => window.location.href = '/accounting/credit-memos' }
  );

  const onSubmit = useCallback(input => {
    setFormStatus('submitting');
    input.type = txn.type;
    updateTxn({ variables: prepareTxnInput(input, fields) });
  });

  const watchAll = watch();

  const addLine = () => {
    const prefix = `txnLinesAttributes.${nestedResources.length}`;

    register(`${prefix}.accountId`, { value: salesAccount?.id });
    register(`${prefix}.debitLessCredit`, { value: '' });
    register(`${prefix}.memo`, { value: '' });
    setNestedResources([...nestedResources, EMPTY_LINE]);
  };

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.debitLessCredit) {
        const prefix = `txnLinesAttributes.${nestedResources.length}`;

        register(`${prefix}.accountId`, { value: '' });
        register(`${prefix}.debitLessCredit`, { value: '' });
        register(`${prefix}.memo`, { value: '' });
        setNestedResources([...nestedResources, EMPTY_LINE]);
      }
    }
  }, [watchAll]);

  const handleDelete = () => {
    if (window.confirm('Really delete credit memo?')) {
      destroyTxn({ variables: { id: txn.id } });
    }
  }

  const canEdit =
    txn.bankDate > DateTime.now().minus({ months: 1 }).toISODate();

  return (
    <View>
      {canEdit && (
        <Pressable onPress={handleDelete}>
          <Text>Delete Customer Credit</Text>
        </Pressable>
      )}

      <FormProvider watch={watch}>
        <Fields
          fields={fields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <NestedFields
          prefix="txnLinesAttributes"
          resources={nestedResources}
          fields={lineFields}
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <Pressable onPress={addLine}>
          <Text style={{ fontSize: 14 }}>+ Add Line</Text>
        </Pressable>

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button
            label="Update Customer Credit"
            style={{ width: 400 }}
            onPress={handleSubmit(onSubmit)}
          />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default EditCreditMemo;
